import * as React from 'react'
import { APP_ONBOARDING, onboardingScreenStates, onboardingSteps, onboardingStepsOrder } from '../utils/constants';
const OnboardingContext = React.createContext()

const initialData = {
    bannerUrl: 'https://firebasestorage.googleapis.com/v0/b/mobilizei.appspot.com/o/themes%2Fclassic%2Ftheme_main_banner.png?alt=media',
    discoverySource: '',
    discoverySourceDetail: '',
    ecommercePlatformId: '',
    ecommercePlatformName: '',
    ecommercePlatformAllowsIframe: false,
    phoneNumber: '',
    storeId: '',
    storeAccountUrl: '',
    storeCartUrl: '',
    storeName: '',
    storeUrl: ''
}

const getDefaultScreenState = (step) => onboardingScreenStates[step]?.[0] || 'default';

// The platform authorizations (Nuvemshop, Wix, Irroba) take the user out of the
// panel and bring him back with a full page reload, which would wipe everything
// collected so far. So the data and the current step are persisted.
const getPersistedOnboarding = () => {
    try {
        const persisted = JSON.parse(localStorage.getItem(APP_ONBOARDING));

        return {
            data: { ...initialData, ...persisted?.data },
            currentStep: persisted?.currentStep || onboardingSteps.PROFILE,
            theme: persisted?.theme || null
        }
    } catch {
        return { data: initialData, currentStep: onboardingSteps.PROFILE, theme: null }
    }
}

function useOnboarding() {
    const context = React.useContext(OnboardingContext);
    if (!context) {
        throw new Error(`useOnboarding must be used within a OnboardingContext`);
    }
    return context;
}

function OnboardingProvider({ children }) {
    const persisted = React.useRef(getPersistedOnboarding()).current;

    const [currentStep, setCurrentStep] = React.useState(persisted.currentStep)

    // The variation of the current screen being rendered (e.g. 'uploaded',
    // 'detected', 'composer'). Reset to the screen's default on every step change.
    const [screenState, setScreenState] = React.useState(getDefaultScreenState(persisted.currentStep))

    const [data, setData] = React.useState(persisted.data);

    // The store's active theme, from the store creation on. It is persisted with
    // the rest so the screens that preview the app (Brand, Preview, Conclude) can
    // render it right away, without each one going after it again.
    const [theme, setTheme] = React.useState(persisted.theme)

    React.useEffect(() => {
        localStorage.setItem(APP_ONBOARDING, JSON.stringify({ data, currentStep, theme }));
    }, [data, currentStep, theme])

    // On mobile the steps are taller than the screen, so a step change would land
    // the user in the middle of the next one. Runs after the new step is rendered,
    // otherwise the browser would clamp the scroll to the previous step's height.
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep])

    const goToStep = (step) => {
        setCurrentStep(step);
        setScreenState(getDefaultScreenState(step));
    }

    const goToNextStep = () => {
        const currentStepIndex = onboardingStepsOrder.findIndex(e => e === currentStep);
        const nextStep = onboardingStepsOrder[currentStepIndex + 1];

        if (nextStep) goToStep(nextStep);
    }

    const goToPreviousStep = () => {
        const currentStepIndex = onboardingStepsOrder.findIndex(e => e === currentStep);
        const previousStep = onboardingStepsOrder[currentStepIndex - 1];

        if (previousStep) goToStep(previousStep);
    }

    const clearOnboardingContext = () => {
        goToStep(onboardingSteps.PROFILE)
        setData(initialData)
        setTheme(null)
    }

    const values = {
        currentStep,
        goToStep,
        goToNextStep,
        goToPreviousStep,
        screenState,
        setScreenState,
        data,
        setData,
        theme,
        setTheme,
        clearOnboardingContext
    }

    return (
        <OnboardingContext.Provider value={values}>
            {children}
        </OnboardingContext.Provider>
    )
}

export { OnboardingContext, OnboardingProvider, useOnboarding }
